import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { SortingLotStockInForm } from './new'
import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateInNoteRequest } from '@/lib/api/sorting-lot-api'
import { sortingNoteItemOption } from '@/components/lots/lot-option-builders'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    useSortingInNote,
    useUpdateSortingInNote,
} from '@/hooks/use-sorting-lot-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sorting-warehouse/stock-in-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useSortingInNote(id)
    const note = data?.data
    const update = useUpdateSortingInNote()

    const seedLotOptions = useMemo(
        () => (note?.items ?? []).map(sortingNoteItemOption),
        [note],
    )

    // Lô chè mua nguồn đã chọn (seed để picker hiện đúng dù ngoài top-search).
    const seedSourceOptions = useMemo<LotOption[]>(
        () =>
            (note?.items ?? []).flatMap((it) =>
                (it.sources ?? []).map((s) => ({
                    value: s.source_purchase_lot_id,
                    label:
                        s.source_purchase_lot_code ?? s.source_purchase_lot_id,
                })),
            ),
        [note],
    )

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/sorting-warehouse/stock-in-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    // Mọi item map thành row 'existing' trỏ vào lô của phiếu. Thêm lô mới (theo
    // grade) chỉ khi user thêm dòng mới trong lúc sửa.
    const initialValues = {
        // Phiếu sửa được chỉ có 3 lý do này (stocktake/legacy bị khoá ở guard trên).
        reason: note.reason as
            | 'adjustment'
            | 'from_sorting_process'
            | 'return_from_scenting',
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            sorting_lot_id: it.sorting_lot_id,
            quantity: it.quantity,
            sources: (it.sources ?? []).map((s) => ({
                source_lot_id: s.source_purchase_lot_id,
                source_quantity: s.source_quantity,
            })),
        })),
    }

    const backToDetail = () =>
        navigate({
            to: '/sorting-warehouse/stock-in-notes/$id',
            params: { id },
        })

    const handleSubmit = async (value: CreateInNoteRequest) => {
        try {
            await update.mutateAsync({
                id,
                req: {
                    reason: value.reason,
                    source_withdrawal_note_id: value.source_withdrawal_note_id,
                    notes: value.notes,
                    items: value.items,
                },
            })
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <SortingLotStockInForm
            mode="edit"
            title={m.sl_pnk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            initialWithdrawalNoteId={
                note.purchase_lot_withdrawal_note_id ?? undefined
            }
            seedLotOptions={seedLotOptions}
            seedSourceOptions={seedSourceOptions}
            isPending={update.isPending}
            onSubmit={(value) => void handleSubmit(value)}
            onCancel={() => void backToDetail()}
        />
    )
}
